import React, { useCallback, useEffect, useState } from "react";
import { Select } from "antd";
import { useHistory, useLocation } from "react-router-dom";
import { Spinner } from "components/ui";
import { Icon } from "components/hds";
import { useTranslation } from "react-i18next";
import styled from "styled-components/macro";

import useProjects from "contexts/Projects/useProjects";
import useOrganizations from "contexts/Organizations/useOrganizations";
import { PROJECTS, SCENES } from "common/routes.constants";
import {
	StyledSwitcherContainer,
	StyledSwitcherNoMatch,
} from "common/styles/Switcher.styles";
import MenuItemStyles from "components/MenuItem/MenuItem.styles";
import MenuItem from "components/MenuItem/MenuItem";
import useConfirmLeaveScene from "hooks/useConfirmLeaveScene";
import useSwitcherSearch, { isUnfilteredQuery } from "hooks/useSwitcherSearch";
import { PROJECT_SEARCH_FIELDS } from "contexts/Projects/searchFields";
import { Project } from "contexts/Projects/types";

const { Option } = Select;

const SEARCH_FIELDS_QUERY = PROJECT_SEARCH_FIELDS.map(
	(field) => `search[fields]=${field}`,
).join("&");

const ProjectSwitcher = () => {
	const history = useHistory();
	const { search, pathname } = useLocation();
	const {
		loading: loadingOrganizations,
		current: currentOrganization,
	} = useOrganizations();
	const {
		current,
		projects,
		loading,
		loadMore,
		query,
		pagedResponse,
	} = useProjects();
	const { t } = useTranslation();
	const confirmLeaveScene = useConfirmLeaveScene();

	const [switcherState, setSwitcherState] = useState("");

	// A personal organization's project shows a translated label, not its name.
	const personalProjectName = currentOrganization?.isPersonal
		? String(t("app.global.b2c.personalProjectName"))
		: undefined;

	// Typing filters the loaded projects at once and asks the server for the
	// rest of the organization's projects (see useSwitcherSearch). A personal
	// organization shows every project under the same translated label, so
	// there the search stays local: null means "don't ask the server".
	const buildSearchUrl = useCallback(
		(text: string) =>
			personalProjectName
				? null
				: `/projects/members/getByProjects?limit=100&page=1&sort=meta.name&noInfo=true&search[text]=${encodeURIComponent(text)}&${SEARCH_FIELDS_QUERY}`,
		[personalProjectName],
	);
	const projectSearch = useSwitcherSearch<Project>({
		buildUrl: buildSearchUrl,
		getLabel: (project) => personalProjectName ?? project.meta.name,
		// The same fields as the projects gallery, so both find the same
		// projects (BMS users search by the end customer's e-mail or number).
		searchFields: PROJECT_SEARCH_FIELDS,
		getKey: (project) => project._id,
		loadedItems: projects,
		loadedComplete:
			Boolean(pagedResponse) &&
			!pagedResponse?.hasNextPage &&
			isUnfilteredQuery(query),
	});
	const serverSearch = projectSearch.isSearching;

	// Also covers the "All projects" entry: it is an Option like any other, so
	// picking it comes through here with an empty shortid. (Its own onClick
	// below only fires for the case where the app is outside /projects
	// entirely — never from the scene editor.)
	const handleProjectChange = (shortid = "") => {
		const destination = shortid
			? `/${PROJECTS}/${shortid}/${SCENES}`
			: `/${PROJECTS}`;

		// Leaving the project leaves the open scene with it, unsaved edits and
		// all — same confirmation as the header logo. On cancel the Select snaps
		// back on its own: `value` follows switcherState, which only moves when
		// `current` does.
		confirmLeaveScene(() => {
			history.push({
				pathname: destination,
				search,
			});
		});
	};

	// click on "All projects" redirects to /projects
	// if current page is outside /projects (e.g. library, profile, etc)
	const handleAllProjectsClick = (
		event: React.MouseEvent<HTMLDivElement, MouseEvent>,
	) => {
		event.preventDefault();

		if (!pathname.includes(PROJECTS)) {
			history.push({
				pathname: `/${PROJECTS}`,
				search,
			});
		}
	};

	useEffect(() => {
		setSwitcherState(current?.shortid || "");
	}, [current]);

	if (loadingOrganizations) {
		return <span>{/* Do not delete this span */}</span>;
	}

	return (
		<StyledSwitcherContainer className="projectSwitcher">
			<MenuItemStyles />
			<span className="label">
				{String(t("app.ProjectSwitcher.label.switcher"))}
			</span>
			<Select
				value={switcherState}
				/* No inline width: it would beat the wider one in
				   Switcher.styles.tsx, which is what carries the chip's hit area
				   under the chevron. */

				onChange={handleProjectChange}
				loading={loading || projectSearch.loading}
				bordered={false}
				popupClassName="header-select-dropdown"
				showSearch
				searchValue={projectSearch.searchText}
				onSearch={projectSearch.setSearchText}
				onOpenChange={(open) => {
					if (!open) {
						projectSearch.reset();
					}
				}}
				filterOption={false}
				notFoundContent={
					projectSearch.loading ? (
						<Spinner maxSize="25px" />
					) : (
						<StyledSwitcherNoMatch>
							{String(t("app.ProjectSwitcher.label.noMatch"))}
						</StyledSwitcherNoMatch>
					)
				}
				/* The list is where names must not truncate, so it sizes to its
				   content. rc-select keeps the trigger width as a minimum; the cap
				   is in MenuItem.styles.tsx. */
				popupMatchSelectWidth={false}
				suffixIcon={<Icon name="ArrowDownFilledIcon" />}
				className="header-select scrolling-popup"
				virtual
				onPopupScroll={(
					event: React.UIEvent<HTMLDivElement, UIEvent>,
				) => {
					const {
						scrollTop,
						offsetHeight,
						scrollHeight,
					} = event.target as HTMLElement;
					const shouldLoadMore =
						scrollTop + offsetHeight === scrollHeight;

					if (shouldLoadMore && !loading && !serverSearch) {
						loadMore();
					}
				}}
			>
				{serverSearch &&
					(projectSearch.results ?? []).map((project) => (
						<Option key={project._id} value={project.shortid}>
							<MenuItem title={personalProjectName ?? project.meta.name} />
						</Option>
					))}
				{!serverSearch && loading && (
					<Option key="loading1" value="">
						<MenuItem title="Loading projects" />
					</Option>
				)}
				{!serverSearch && currentOrganization && !loading && !currentOrganization?.isPersonal && (
					<Option value="">
						<StyledMenuItem
							className="switcher-item"
							title={String(t("app.ProjectSwitcher.label.allProjects"))}
							onClick={handleAllProjectsClick}
						/>
					</Option>
				)}
				{!serverSearch &&
					projects.map((project) => (
						<Option key={project._id} value={project.shortid}>
							<MenuItem title={personalProjectName ?? project.meta.name} />
						</Option>
					))}
				{!serverSearch && loading && (
					<Option key="loading2" value="">
						<div style={{ marginTop: "10px" }}>
							<Spinner maxSize="25px" />
						</div>
					</Option>
				)}
			</Select>
		</StyledSwitcherContainer>
	);
};

const StyledMenuItem = styled(MenuItem)`
	padding: 0 10px;
`;
export default ProjectSwitcher;
